import { products, type TechProduct } from "@/mocks/techData";

function Tile({
  p,
  onOpen,
  aspect,
}: {
  p: TechProduct;
  onOpen: (p: TechProduct) => void;
  aspect: string;
}) {
  return (
    <button
      onClick={() => onOpen(p)}
      className="group relative block h-full w-full cursor-pointer overflow-hidden rounded-lg bg-foreground-950 text-left"
    >
      <div className={`${aspect} overflow-hidden`}>
        <img
          src={p.images[0]}
          alt={p.name}
          loading="lazy"
          className="h-full w-full object-cover object-center transition-transform duration-[900ms] ease-out group-hover:scale-105"
        />
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/85 to-transparent p-4 md:p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-heading text-base text-background-50 md:text-lg">
              {p.name}
            </h3>
            <p className="mt-0.5 truncate text-xs text-background-100/70">{p.availability}</p>
          </div>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-foreground-950 transition-transform duration-300 group-hover:-translate-y-1">
            <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </button>
  );
}

export default function ProductShowcase({ onOpen }: { onOpen: (p: TechProduct) => void }) {
  return (
    <section id="products" className="bg-foreground-50 py-20 md:py-28">
      <div className="container-x">
        {/* Section header */}
        <div className="mb-12 md:mb-16">
          <p className="eyebrow mb-4 flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-foreground-950"></span>
            The Showcase
          </p>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <h2 className="section-title max-w-2xl">Technology worth exploring.</h2>
            <a href="#visit" className="btn-ghost w-fit h-11 shrink-0 px-5 text-xs">
              Enquire About a Product <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid auto-rows-auto gap-4 md:gap-5 lg:grid-cols-12">
          {/* Hero tile */}
          <div className="lg:col-span-7 lg:row-span-2">
            <Tile
              p={products[0]}
              onOpen={onOpen}
              aspect="aspect-[4/3] sm:aspect-[16/13] lg:h-full"
            />
          </div>
          {/* Right stack */}
          <div className="lg:col-span-5">
            <Tile p={products[1]} onOpen={onOpen} aspect="aspect-[4/3] md:aspect-[16/10]" />
          </div>
          <div className="lg:col-span-5">
            <Tile p={products[2]} onOpen={onOpen} aspect="aspect-[4/3] md:aspect-[16/10]" />
          </div>
          {/* Medium row */}
          <div className="lg:col-span-4">
            <Tile p={products[3]} onOpen={onOpen} aspect="aspect-[4/5] sm:aspect-[3/2]" />
          </div>
          <div className="lg:col-span-4">
            <Tile p={products[4]} onOpen={onOpen} aspect="aspect-[4/5] sm:aspect-[3/2]" />
          </div>
          <div className="lg:col-span-4">
            <Tile p={products[5]} onOpen={onOpen} aspect="aspect-[4/5] sm:aspect-[3/2]" />
          </div>
          {/* Wide row */}
          <div className="lg:col-span-6">
            <Tile p={products[6]} onOpen={onOpen} aspect="aspect-[16/9] sm:aspect-[2/1]" />
          </div>
          <div className="lg:col-span-6">
            <Tile p={products[7]} onOpen={onOpen} aspect="aspect-[16/9] sm:aspect-[2/1]" />
          </div>
        </div>
      </div>
    </section>
  );
}